import agwaLockLostBannerStyles from './agwa-lock-lost-banner.styles.css?inline';
import { LitElement, html, unsafeCSS } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '../user/username.js';

/**
 * `agwa-lock-lost-banner` - the one way a page tells the person that the lock they were working
 * under is gone.
 *
 * Losing a lock is the moment unsaved work stops being saveable, and until this existed the pages
 * that lost one either said nothing (Glass) or dropped edit mode and reloaded without a word
 * (Product Registry), so the person found their changes missing and did not know why (@LOCK-010,
 * @LOCK-011, D-115). It is PERSISTENT: the five-second alerts the pages use for refusals auto-hide,
 * and a loss read after the alert has gone is a loss nobody was told about. The person dismisses it
 * (`dismiss` event) when they have read it.
 *
 * Two losses, two sentences. A takeover names who holds the lock now, because that is who to talk
 * to; a release from elsewhere (the session ended in another tab, the lease lapsed) names nobody,
 * because nobody holds it. The host decides which by whether the lock store reports a holder.
 *
 * One component per concept (CLAUDE.md): the Glass certificate pages and the Product Registry
 * report and batch pages all mount this same element, so the wording cannot drift between modules
 * and a journey can pin one selector.
 */
@customElement('agwa-lock-lost-banner')
export class AgwaLockLostBanner extends LitElement {
  static styles = unsafeCSS(agwaLockLostBannerStyles);

  /** How the locked thing is named to the person: "certificate", "report", "batch". */
  @property({ type: String }) noun = 'record';

  /**
   * Login id of whoever took the lock over; null or empty when it was released, not taken. The
   * person is named through the directory by this id, as everywhere a person is named (D-151,
   * contact rule 8), so the banner shows their display name with their card behind it.
   */
  @property({ type: String, attribute: 'taken-over-by-user-id' })
  takenOverByUserId: string | null = null;

  render() {
    const how = this.takenOverByUserId
      ? html`was taken over by
          <strong
            ><user-name .userId=${this.takenOverByUserId}></user-name></strong
          >.`
      : html`was released from another tab or session.`;
    return html`<sl-alert
      variant="danger"
      open
      closable
      @sl-after-hide=${this.onDismiss}
    >
      <sl-icon slot="icon" name="lock-fill"></sl-icon>
      <p class="message">
        Your lock on this ${this.noun} ${how} Changes you have not saved can no
        longer be saved.
      </p>
    </sl-alert>`;
  }

  private onDismiss = (): void => {
    this.dispatchEvent(
      new CustomEvent('dismiss', { bubbles: true, composed: true })
    );
  };
}

declare global {
  interface HTMLElementTagNameMap {
    'agwa-lock-lost-banner': AgwaLockLostBanner;
  }
}
